// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as FileTextIcon } from '../../../../assets/Icons/lucideIcons/file-text.svg';
import { ReactComponent as VisibilityIcon } from '../../../../assets/Icons/lucideIcons/eye.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';

import DocumentPreviewModal from '../../../../features/document/components/DocumentPreviewModal';
import useAttributeDocumentPreview from '../../../../hooks/useAttributeDocumentPreview';
import useModalDisclosure from '../../../../hooks/useModalDisclosure';
import MultiDocumentFieldComponent from '../FieldComponents/MultiDocumentFieldComponent';
import { NOOP } from '../../../../const/CommonConst';

const ViewDocumentAttribute = ({
  documentDetails,
  attribute,
  configColumn = false,
  entityId,
  entityType,
}) => {
  const theme = useTheme();
  const {
    open: isModalOpen,
    handleOpen: openModal,
    handleClose: closeModal,
  } = useModalDisclosure();

  const { previewDocument, previewModalProps } = useAttributeDocumentPreview({
    entityId,
    entityType,
  });

  // Normalize documentDetails to an array
  const documents = useMemo(() => {
    if (!documentDetails) return [];
    const arr = Array.isArray(documentDetails)
      ? documentDetails
      : [documentDetails];
    return arr.filter((doc) =>
      Boolean(
        doc?.document_id ||
        doc?.document_name ||
        (typeof doc !== 'object' && doc)
      )
    );
  }, [documentDetails]);

  const isMultiDoc = useMemo(
    () => attribute?.input_control_type?.toUpperCase() === 'MULTI_DOCUMENT',
    [attribute]
  );

  const getDocumentFullName = useCallback((doc, index) => {
    if (!doc) return `Document ${index + 1}`;
    if (typeof doc === 'string' || typeof doc === 'number') {
      return String(doc);
    }
    const name = doc.document_name || doc.file_name || '';
    const type = doc.document_type || doc.file_type || '';
    if (!name) return `Document ${index + 1}`;
    if (type && !name.toLowerCase().endsWith(`.${type.toLowerCase()}`)) {
      return `${name}.${type}`;
    }
    return name;
  }, []);

  const singleDoc = documents[0];
  const singleDocId =
    typeof singleDoc === 'object' ? singleDoc?.document_id : singleDoc;
  const singleDocFullName = getDocumentFullName(singleDoc, 0);

  const handleSingleDocClick = useCallback(() => {
    if (singleDocId) {
      previewDocument(singleDocId, singleDoc);
    }
  }, [singleDocId, singleDoc, previewDocument]);

  if (documents.length === 0) {
    return <Typography variant="subtitle">-</Typography>;
  }

  const attributeTitle =
    attribute?.label || attribute?.name || 'Attached Documents';

  // Multi-document view rendering
  if (isMultiDoc) {
    return (
      <Box>
        <Button
          variant="text"
          onClick={openModal}
          sx={{
            p: 0,
            minWidth: 'auto',
            height: 'auto !important',
            color: 'primary.main',
            textDecoration: 'underline',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.43,
            display: 'inline-flex',
            verticalAlign: 'baseline',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '2px',
            },
          }}
        >
          View {attributeTitle} ({documents.length})
        </Button>

        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          maxWidth="md"
          fullWidth
          aria-labelledby="view-multi-doc-dialog-title"
          PaperProps={{
            sx: {
              borderRadius: '8px',
              maxHeight: '70vh',
            },
          }}
        >
          {/* Dialog Header */}
          <DialogTitle
            id="view-multi-doc-dialog-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {attributeTitle} ({documents.length})
            </Typography>

            <IconButton
              aria-label="close"
              onClick={closeModal}
              size="small"
              sx={{ color: 'text.secondary', p: 0.5 }}
            >
              <CloseIcon width={18} height={18} />
            </IconButton>
          </DialogTitle>
          <Divider />

          {/* Dialog Body Content - Unified MultiDocumentFieldComponent in view mode */}
          <DialogContent sx={{ p: 3 }}>
            <MultiDocumentFieldComponent
              value={documents}
              attribute={attribute}
              mode="view"
              onChange={NOOP}
              entityData={{ entity_id: entityId, entity_type: entityType }}
            />
          </DialogContent>
          <Divider />

          {/* Dialog Actions Footer */}
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeModal} variant="contained" size="medium">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Single document view mode (inline)
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '95%' }}
    >
      <FileTextIcon
        color={theme.palette.primary.main}
        width={18}
        height={18}
        style={{ flexShrink: 0, color: theme.palette.primary.main }}
      />
      <Typography
        color="primary"
        onClick={handleSingleDocClick}
        sx={{
          flex: '0 1 auto',
          minWidth: 0,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
          ...(configColumn
            ? {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }
            : {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }),
        }}
        title={singleDocFullName}
      >
        {singleDocFullName}
      </Typography>
      <IconButton
        size="small"
        title="Preview document"
        onClick={handleSingleDocClick}
        sx={{ flexShrink: 0 }}
      >
        <VisibilityIcon
          width={18}
          height={18}
          style={{ color: theme.palette.primary.main }}
        />
      </IconButton>

      {/* Document Preview Modal */}
      <DocumentPreviewModal {...previewModalProps} />
    </Box>
  );
};

ViewDocumentAttribute.propTypes = {
  documentDetails: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        document_name: PropTypes.string,
        document_type: PropTypes.string,
        document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    PropTypes.shape({
      document_name: PropTypes.string,
      document_type: PropTypes.string,
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  attribute: PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    input_control_type: PropTypes.string,
    inputType: PropTypes.string,
    control_type: PropTypes.string,
    attribute_type: PropTypes.string,
  }),
  configColumn: PropTypes.bool,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.string,
};

export default ViewDocumentAttribute;
